/**
 * Banner Store API Configuration
 * Backend integration for banner stores service using the common API service
 */

import { API } from 'services/common'

// Backend response interfaces
export interface BannerStoreInstructions {
  bannerStoreId: string
  name: string
  retailerId: number
  /** CDN URL for store logo - accessible from both web and email */
  logoUrl: string
  barcodeFormat: 'CODE_128' | 'QR_CODE'
  hideBarcodeImage?: boolean // For walmart-online, show only code text

  digitalPaymentMessage: {
    title: string
    description: string
  }
  cashierInstructions: {
    title: string
    description: string
  }
}

export interface StoreGroup {
  storeGroupId: number
  storeGroupName: string
  bannerStores: BannerStoreInstructions[]
}

// API Client using the common API service
class BannerStoreApiClient {
  async getAllBannerStores(): Promise<BannerStoreInstructions[]> {
    try {
      const response = await API.doGet2('/banner-stores', {})
      if (response.ok) {
        return response.json()
      }
      throw new Error(`API Error: ${response.status}`)
    } catch (error) {
      console.error('Failed to fetch all banner stores:', error)
      return []
    }
  }

  async getBannerStoreById(
    id: string,
  ): Promise<BannerStoreInstructions | null> {
    try {
      const response = await API.doGet2(`/banner-stores/${id}`, {})
      if (response.status === 404) {
        return null
      }
      if (response.ok) {
        return response.json()
      }
      throw new Error(`API Error: ${response.status}`)
    } catch (error) {
      console.error(`Failed to fetch banner store ${id}:`, error)
      return null
    }
  }

  async getBannerStoresByIds(
    ids: string[],
  ): Promise<BannerStoreInstructions[]> {
    if (ids.length === 0) return []

    try {
      const response = await API.doGet2('/banner-stores/by-ids', {
        ids: ids.join(','),
      })
      if (response.ok) {
        return response.json()
      }
      throw new Error(`API Error: ${response.status}`)
    } catch (error) {
      console.error('Failed to fetch banner stores by IDs:', error)
      return []
    }
  }

  async getBannerStoresByRetailerIds(
    retailerIds: number[],
  ): Promise<BannerStoreInstructions[]> {
    if (retailerIds.length === 0) return []

    try {
      const response = await API.doGet2('/banner-stores/by-retailer-ids', {
        retailerIds: retailerIds.join(','),
      })
      if (response.ok) {
        return response.json()
      }
      throw new Error(`API Error: ${response.status}`)
    } catch (error) {
      console.error('Failed to fetch banner stores by retailer IDs:', error)
      return []
    }
  }

  async getStoreGroups(): Promise<StoreGroup[]> {
    try {
      const response = await API.doGet2('/banner-stores/store-groups', {})
      if (response.ok) {
        return response.json()
      }
      throw new Error(`API Error: ${response.status}`)
    } catch (error) {
      console.error('Failed to fetch store groups:', error)
      return []
    }
  }

  async normalizeStoreIds(storeIds: string[]): Promise<string[]> {
    if (storeIds.length === 0) return []

    try {
      const response = await API.doGet2('/banner-stores/normalize-ids', {
        storeIds: storeIds.join(','),
      })
      if (response.ok) {
        return response.json()
      }
      throw new Error(`API Error: ${response.status}`)
    } catch (error) {
      console.error('Failed to normalize store IDs:', error)
      return storeIds // Return original IDs as fallback
    }
  }
}

// Create singleton instance
const bannerStoreApiClient = new BannerStoreApiClient()

/**
 * BANNER STORES CONFIGURATION
 *
 * This module is based on banner stores (not parent retailers).
 * Multiple banner stores may share the same cashier instructions but have different logos.
 *
 * USAGE WITH API DATA:
 *
 * The InComm API returns data with an availableStoreIds array:
 *
 * Example API Response:
 * {
 *   "availableStoreIds": ["cvs", "walgreens"],
 *   "ctaText": "test123",
 *   "description": "<p>test test </p>",
 *   "id": "step-1759442299281",
 *   "legalDescription": "<p>test 123</p>",
 *   "offerBannerImage": "cd2b0b6f-8553-463d-898b-2e2feaf4a713",
 *   "offerId": 5,
 *   "offerNumber": "1893660000",
 *   "supportDescription": "<p>test 123</p>",
 *   "title": "test",
 *   "type": null
 * }
 *
 * Usage Example:
 * ```typescript
 * // Get single store (async)
 * const store = await getBannerStoreById("cvs");
 *
 * // Get multiple stores from API response (async)
 * const stores = await getBannerStoresByIds(apiResponse.availableStoreIds);
 * ```
 *
 * SUPPORTED BANNER STORES:
 *
 * All banner stores are now loaded dynamically from the backend API.
 * The configuration includes Walmart, Kroger Family, Albertsons Safeway,
 * CVS, Walgreens, Harps, Giant Eagle, and other retailers.
 */

/**
 * Get a single banner store by its ID
 * @param retailerId - The store ID from availableStoreIds (e.g., "cvs", "walgreens")
 * @returns Promise resolving to the retailer instructions or null if not found
 */
export const getBannerStoreById = async (
  retailerId: string,
): Promise<BannerStoreInstructions | null> => {
  try {
    return await bannerStoreApiClient.getBannerStoreById(retailerId)
  } catch (error) {
    console.error(`Failed to fetch banner store ${retailerId}:`, error)
    return null
  }
}

/**
 * Get multiple banner stores by their IDs
 * Useful for processing the availableStoreIds array from the API
 * @param storeIds - Array of store IDs (e.g., ["cvs", "walgreens"])
 * @returns Promise resolving to array of retailer instructions (filters out any invalid IDs)
 *
 * @example
 * const stores = await getBannerStoresByIds(["cvs", "walgreens"])
 * // Returns array of RetailerInstructions for CVS and Walgreens
 */
export const getBannerStoresByIds = async (
  storeIds: string[],
): Promise<BannerStoreInstructions[]> => {
  try {
    return await bannerStoreApiClient.getBannerStoresByIds(storeIds)
  } catch (error) {
    console.error('Failed to fetch banner stores by IDs:', error)
    return []
  }
}

/**
 * Get all available banner stores
 * @returns Promise resolving to array of all banner stores
 */
export const getAllBannerStores = async (): Promise<
  BannerStoreInstructions[]
> => {
  try {
    return await bannerStoreApiClient.getAllBannerStores()
  } catch (error) {
    console.error('Failed to fetch all banner stores:', error)
    return []
  }
}

/**
 * Get banner stores by retailer IDs
 * @param retailerIds - Array of retailer IDs
 * @returns Promise resolving to array of banner stores
 */
export const getBannerStoresByRetailerIds = async (
  retailerIds: number[],
): Promise<BannerStoreInstructions[]> => {
  try {
    return await bannerStoreApiClient.getBannerStoresByRetailerIds(retailerIds)
  } catch (error) {
    console.error('Failed to fetch banner stores by retailer IDs:', error)
    return []
  }
}

/**
 * Get all store groups (organized by retailer)
 * @returns Promise resolving to array of store groups
 */
export const getStoreGroups = async (): Promise<StoreGroup[]> => {
  try {
    return await bannerStoreApiClient.getStoreGroups()
  } catch (error) {
    console.error('Failed to fetch store groups:', error)
    return []
  }
}

/**
 * Normalize legacy store IDs to current banner store IDs
 * @param storeIds - Array of store IDs (legacy or current)
 * @returns Promise resolving to array of normalized store IDs
 */
export const normalizeStoreIds = async (
  storeIds: string[],
): Promise<string[]> => {
  try {
    return await bannerStoreApiClient.normalizeStoreIds(storeIds)
  } catch (error) {
    console.error('Failed to normalize store IDs:', error)
    return storeIds // Return original IDs as fallback
  }
}

/**
 * Get all available banner store IDs
 * @returns Promise resolving to array of all store IDs
 */
export const getBannerStoreIds = async (): Promise<string[]> => {
  try {
    const stores = await bannerStoreApiClient.getAllBannerStores()
    return stores.map(store => store.bannerStoreId)
  } catch (error) {
    console.error('Failed to fetch banner store IDs:', error)
    return []
  }
}
